// src/components/job-engine/JobEngineDashboardPage.tsx
import React, { useState } from "react";
import { Stack, Dropdown, IDropdownOption, MessageBar, MessageBarType } from "@fluentui/react";
import { useDashboardTiles } from "./useDashboardTiles";
import { useDashboardCharts } from "./useDashboardCharts";
import DashboardTiles from "./DashboardTiles";
import DashboardCharts from "./DashboardCharts";
import useJobEngineEvent from "./useJobEngineEvent";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import ConnectionStatusBadge from "./ConnectionStatusBadge";

const windowOptions: IDropdownOption[] = [
  { key: "1h",  text: "Last 1 hour" },
  { key: "24h", text: "Last 24 hours" },
];

const JobEngineDashboardPage: React.FC = () => {
  const [win, setWin] = useState<"1h" | "24h">("1h");
  const tiles = useDashboardTiles();
  const charts = useDashboardCharts(win);

  useJobEngineEvent("JobStarted",      tiles.refetchThrottled);
  useJobEngineEvent("JobFinished",     tiles.refetchThrottled);
  useJobEngineEvent("JobFailed",       tiles.refetchThrottled);
  useJobEngineEvent("JobDeadLettered", tiles.refetchThrottled);

  const isConnected = useJobEngineConnectionState();
  React.useEffect(() => {
    if (isConnected) return;
    const id = window.setInterval(() => tiles.refetch(), 10_000);
    return () => window.clearInterval(id);
  }, [isConnected, tiles]);

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
    <Stack tokens={{ childrenGap: 16 }}>
      <div className="page-header">
        <h2 className="page-title">Dashboard</h2>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <Dropdown
            options={windowOptions}
            selectedKey={win}
            onChange={(_, o) => o && setWin(o.key as "1h" | "24h")}
            styles={{ root: { width: 180 } }}
          />
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>
      {!isConnected && (
        <MessageBar messageBarType={MessageBarType.warning}>
          Live updates unavailable — polling.
        </MessageBar>
      )}
      {tiles.error && <MessageBar messageBarType={MessageBarType.error}>{tiles.error}</MessageBar>}
      <DashboardTiles data={tiles.data} />
      {charts.error && <MessageBar messageBarType={MessageBarType.error}>{charts.error}</MessageBar>}
      <DashboardCharts data={charts.data} />
    </Stack>
    </div>
  );
};

export default JobEngineDashboardPage;
